// getPrivateShowTicketAmount.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import { SHOW_FUNDING_OPTIONS_MIDDLE } from '../RemoteConfigKeys.js';
import getGoalData from '../selector/getGoalData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import {
  privateShowGiftType,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';

/**
 * Select private show ticket amount by stream id and gift type.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @param {string} fundingId - funding id.
 * @param {string} mvpUserId - mvp user id.
 * @param {string} meId - me id.
 * @param {string} giftType - stream gift type.
 * @return {number} The selected private show ticket amount.
 */
const getPrivateShowTicketAmount = createCachedSelector(
  (state, streamId) => getPrivateShowStatus(state, streamId),
  (state, streamId, fundingId, mvpUserId, meId, giftType) => giftType,
  (state, streamId, fundingId, mvpUserId, meId) =>
    getGoalData(state, [fundingId, 'breakdown', meId], 'progress'),
  (state, streamId, fundingId, mvpUserId) =>
    getGoalData(state, [fundingId, 'breakdown', mvpUserId], 'progress'),
  (state, streamId, fundingId) =>
    getGoalData(state, [fundingId, 'levels', 0], 'target'),
  (state, streamId, fundingId) => getGoalData(state, [fundingId], 'progress'),
  state => getRemoteConfigData(state, SHOW_FUNDING_OPTIONS_MIDDLE),
  (
    privateShowStatus,
    giftType,
    meProgress = 0,
    mvpProgress = 0,
    target = 0,
    progress = 0,
    moreAmount = 1
  ) => {
    switch (giftType) {
      case privateShowGiftType.MVP:
        return Math.max(1, mvpProgress - meProgress + 1);
      case privateShowGiftType.MORE:
        if (
          [
            privateShowStatusConstant.PERFORMING,
            privateShowStatusConstant.PREPARING,
          ].includes(privateShowStatus)
        ) {
          return moreAmount;
        }
        return Math.max(1, target - progress);
      case privateShowGiftType.SINGLE:
        return 1;
    }
  }
)(
  (state, streamId, fundingId, mvpUserId, meId, giftType) =>
    `${streamId}:${fundingId}:${mvpUserId}:${meId}:${giftType}`
);

export default getPrivateShowTicketAmount;
